import type {
  ArchivoBucket,
  Colegio,
  ColegioConTotalEvaluaciones,
  Estudiante,
  EstudianteConEstado,
  Evaluacion,
  EvaluacionConProgreso,
  EvaluacionParaFacilitador,
  EvaluacionPublica,
  EstadoEvaluacion,
  Formulario,
  FormularioConSecciones,
  FormularioConTotalPreguntas,
  SeccionInput,
  Grupo,
  GrupoConProgreso,
  GrupoRespuestas,
  ImportacionEstudiantes,
  ContenidoSeccionesInput,
  PreguntaSesion,
  Reporte,
  ReporteConContexto,
  Sesion,
  CategoriaFormulario,
  TipoReporte,
} from "../utils/types";

// Todas las rutas del backend responden { data: T } en éxito,
// { data, mensaje } cuando además hay un mensaje para el usuario,
// o { error, ...detalle } (status >= 400) en caso de fallo.
interface ApiEnvelope<T> {
  data: T;
  mensaje?: string;
}

export interface DirectorioReportes {
  files: ArchivoBucket[];
  folders: string[];
}

export class ApiError extends Error {
  status: number;
  detalle: unknown;

  constructor(status: number, message: string, detalle?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detalle = detalle;
  }
}

const COLEGIO_ID_KEY = "psyeva.colegioId";
const TOKEN_KEY = "psyeva.token";

function buildQuery(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, value);
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

// Centraliza todas las llamadas al API de PSYEVA: arma la URL, adjunta
// el header X-Colegio-Id de la sesión del facilitador cuando aplica,
// parsea la respuesta y normaliza los errores en ApiError.
// Las páginas y componentes solo deben hablar con esta instancia,
// nunca hacer fetch() por su cuenta.
class DatabaseService {
  private colegioId: string | null = null;
  private token: string | null = null;

  constructor() {
    if (typeof localStorage !== "undefined") {
      this.colegioId = localStorage.getItem(COLEGIO_ID_KEY);
      this.token = localStorage.getItem(TOKEN_KEY);
    }
  }

  // ── Sesión del facilitador (clave de acceso del colegio) ────
  get colegioActualId(): string | null {
    return this.colegioId;
  }

  get haySesionFacilitador(): boolean {
    return this.colegioId !== null;
  }

  private guardarSesionFacilitador(colegioId: string, token: string) {
    this.colegioId = colegioId;
    this.token = token;
    localStorage.setItem(COLEGIO_ID_KEY, colegioId);
    localStorage.setItem(TOKEN_KEY, token);
  }

  cerrarSesionFacilitador() {
    this.colegioId = null;
    this.token = null;
    localStorage.removeItem(COLEGIO_ID_KEY);
    localStorage.removeItem(TOKEN_KEY);
  }

  // ── Núcleo HTTP ───────────────────────────────────────────────
  private async request<T>(
    path: string,
    init: RequestInit = {},
    { conColegio = true }: { conColegio?: boolean } = {}
  ): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    if (conColegio && this.colegioId) {
      headers.set("X-Colegio-Id", this.colegioId);
    }

    const res = await fetch(path, { ...init, headers, credentials: "same-origin" });
    const isJson = res.headers.get("Content-Type")?.includes("application/json") ?? false;
    const body = isJson ? await res.json().catch(() => null) : null;

    if (!res.ok) {
      const message = body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : `Error ${res.status}`;
      throw new ApiError(res.status, message, body);
    }

    return body as T;
  }

  private get<T>(path: string, opts?: { conColegio?: boolean }) {
    return this.request<T>(path, { method: "GET" }, opts);
  }
  private post<T>(path: string, body?: Record<string, unknown> | FormData, opts?: { conColegio?: boolean }) {
    return this.request<T>(
      path,
      { method: "POST", body: body instanceof FormData ? body : JSON.stringify(body ?? {}) },
      opts
    );
  }
  private patch<T>(path: string, body?: Record<string, unknown>, opts?: { conColegio?: boolean }) {
    return this.request<T>(path, { method: "PATCH", body: JSON.stringify(body ?? {}) }, opts);
  }
  private del<T>(path: string, opts?: { conColegio?: boolean }) {
    return this.request<T>(path, { method: "DELETE" }, opts);
  }

  // ── Admin ─────────────────────────────────────────────────────
  publico = {
    listarEvaluaciones: async (): Promise<EvaluacionPublica[]> => {
      const { data } = await this.get<ApiEnvelope<EvaluacionPublica[]>>(
        "/api/evaluaciones-publicas",
        { conColegio: false }
      );
      return data;
    },
  };

  // Las rutas /api/admin/* no requieren X-Colegio-Id.
  admin = {
    iniciarSesion: async (password: string): Promise<void> => {
      await this.post<ApiEnvelope<{ autenticado: true }>>("/api/admin/login", { password }, { conColegio: false });
    },

    verificarSesion: async (): Promise<boolean> => {
      const { data } = await this.get<ApiEnvelope<{ autenticado: boolean }>>(
        "/api/admin/sesion",
        { conColegio: false }
      );
      return data.autenticado;
    },

    cerrarSesion: async (): Promise<void> => {
      await this.post<ApiEnvelope<{ autenticado: false }>>("/api/admin/logout", {}, { conColegio: false });
    },

    listarColegios: async (): Promise<ColegioConTotalEvaluaciones[]> => {
      const { data } = await this.get<ApiEnvelope<ColegioConTotalEvaluaciones[]>>(
        "/api/admin/colegios",
        { conColegio: false }
      );
      return data;
    },

    crearColegio: async (input: { nombre: string; claveAcceso: string }): Promise<Colegio> => {
      const { data } = await this.post<ApiEnvelope<Colegio>>("/api/admin/colegios", input, { conColegio: false });
      return data;
    },

    actualizarColegio: async (id: string, cambios: Partial<{ nombre: string; claveAcceso: string }>): Promise<Colegio> => {
      const { data } = await this.patch<ApiEnvelope<Colegio>>(`/api/admin/colegios/${id}`, cambios, { conColegio: false });
      return data;
    },

    listarFormularios: async (): Promise<FormularioConTotalPreguntas[]> => {
      const { data } = await this.get<ApiEnvelope<FormularioConTotalPreguntas[]>>(
        "/api/admin/formularios",
        { conColegio: false }
      );
      return data;
    },

    crearFormulario: async (input: {
      titulo: string;
      descripcion?: string;
      categoria: CategoriaFormulario;
      secciones: SeccionInput[];
    }): Promise<Formulario> => {
      const { data } = await this.post<ApiEnvelope<Formulario>>("/api/admin/formularios", input, { conColegio: false });
      return data;
    },

    obtenerFormulario: async (id: string): Promise<FormularioConSecciones> => {
      const { data } = await this.get<ApiEnvelope<FormularioConSecciones>>(
        `/api/admin/formularios/${id}`,
        { conColegio: false }
      );
      return data;
    },

    // Si se envía `secciones`, reemplaza el set completo (409 si alguna
    // pregunta actual ya tiene respuestas registradas). `contenidoSecciones`
    // actualiza textos e imágenes sin reemplazar IDs ni respuestas.
    actualizarFormulario: async (
      id: string,
      cambios: Partial<{
        titulo: string;
        descripcion: string;
        categoria: CategoriaFormulario;
        secciones: SeccionInput[];
        contenidoSecciones: ContenidoSeccionesInput[];
      }>
    ): Promise<Formulario> => {
      const { data } = await this.patch<ApiEnvelope<Formulario>>(`/api/admin/formularios/${id}`, cambios, { conColegio: false });
      return data;
    },

    // Imágenes predefinidas para el selector de imagen de secciones/preguntas
    // (ver src/components/SelectorImagen.tsx). `carpeta` debe ser uno de los
    // prefijos que el backend tiene en su allowlist.
    listarImagenes: async (carpeta: string): Promise<ArchivoBucket[]> => {
      const { data } = await this.get<ApiEnvelope<ArchivoBucket[]>>(
        `/api/admin/imagenes${buildQuery({ carpeta })}`,
        { conColegio: false }
      );
      return data;
    },

    // Sube una o varias imágenes nuevas al catálogo (sección "Imágenes de formularios"
    // de Configuración). `carpeta` debe ser uno de los prefijos permitidos.
    subirImagen: async (input: { carpeta: string; archivo: File } | { carpeta: string; archivos: File[] }): Promise<ArchivoBucket | ArchivoBucket[]> => {
      const archivos = "archivo" in input ? [input.archivo] : input.archivos;
      const form = new FormData();
      form.set("carpeta", input.carpeta);
      archivos.forEach(archivo => form.append("archivo", archivo));
      const { data } = await this.post<ApiEnvelope<ArchivoBucket | ArchivoBucket[]>>("/api/admin/imagenes", form, { conColegio: false });
      return data;
    },

    eliminarImagen: async (carpeta: string, key: string): Promise<{ key: string; eliminado: boolean }> => {
      const { data } = await this.del<ApiEnvelope<{ key: string; eliminado: boolean }>>(
        `/api/admin/imagenes${buildQuery({ carpeta, key })}`,
        { conColegio: false }
      );
      return data;
    },

    eliminarFormulario: async (id: string): Promise<{ id: string; eliminado: boolean }> => {
      const { data } = await this.del<ApiEnvelope<{ id: string; eliminado: boolean }>>(
        `/api/admin/formularios/${id}`,
        { conColegio: false }
      );
      return data;
    },

    listarEvaluaciones: async (): Promise<EvaluacionConProgreso[]> => {
      const { data } = await this.get<ApiEnvelope<EvaluacionConProgreso[]>>(
        "/api/admin/evaluaciones",
        { conColegio: false }
      );
      return data;
    },

    crearEvaluacion: async (input: { colegioId: string; nombre: string; fecha: string }): Promise<Evaluacion> => {
      const { data } = await this.post<ApiEnvelope<Evaluacion>>(
        "/api/admin/evaluaciones",
        input,
        { conColegio: false }
      );
      return data;
    },

    obtenerEvaluacion: async (id: string): Promise<EvaluacionConProgreso & { grupos: GrupoConProgreso[] }> => {
      const { data } = await this.get<ApiEnvelope<EvaluacionConProgreso & { grupos: GrupoConProgreso[] }>>(
        `/api/admin/evaluaciones/${id}`,
        { conColegio: false }
      );
      return data;
    },

    actualizarEvaluacion: async (
      id: string,
      cambios: Partial<{ nombre: string; fecha: string; estado: EstadoEvaluacion }>
    ): Promise<Evaluacion> => {
      const { data } = await this.patch<ApiEnvelope<Evaluacion>>(
        `/api/admin/evaluaciones/${id}`,
        cambios,
        { conColegio: false }
      );
      return data;
    },

    eliminarEvaluacion: async (id: string): Promise<{ id: string; eliminado: boolean }> => {
      const { data } = await this.del<ApiEnvelope<{ id: string; eliminado: boolean }>>(
        `/api/admin/evaluaciones/${id}`,
        { conColegio: false }
      );
      return data;
    },

    cambiarEstadoEvaluacion: async (
      id: string,
      estado: EstadoEvaluacion
    ): Promise<{ evaluacion: Evaluacion; mensaje: string }> => {
      const { data, mensaje } = await this.patch<ApiEnvelope<Evaluacion>>(
        `/api/admin/evaluaciones/${id}/estado`,
        { estado },
        { conColegio: false }
      );
      return { evaluacion: data, mensaje: mensaje ?? "" };
    },

    // Descarga el .xlsx de respuestas; el caller decide cómo ofrecerlo (link, guardar, etc.)
    exportarEvaluacion: async (id: string, grupoId: string, categoria?: CategoriaFormulario): Promise<{ blob: Blob; filename: string }> => {
      const res = await fetch(`/api/admin/evaluaciones/${id}/exportar${buildQuery({ grupoId, categoria })}`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message = body && typeof body === "object" && "error" in body ? String(body.error) : `Error ${res.status}`;
        throw new ApiError(res.status, message, body);
      }
      const disposition = res.headers.get("Content-Disposition") ?? "";
      const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `evaluacion-${id}.xlsx`;
      return { blob: await res.blob(), filename };
    },

    crearGrupo: async (input: {
      evaluacionId: string;
      nombre: string;
      formEmocionesId?: string;
      formBienpsicId?: string;
      formAprendizajeId?: string;
      estudiantes?: Array<{ nombreCompleto: string; curp?: string }>;
    }): Promise<Grupo & { estudiantes: Estudiante[] }> => {
      const { data } = await this.post<ApiEnvelope<Grupo & { estudiantes: Estudiante[] }>>(
        "/api/admin/grupos",
        input,
        { conColegio: false }
      );
      return data;
    },

    agregarEstudiante: async (
      grupoId: string,
      input: { nombreCompleto: string; curp?: string }
    ): Promise<Estudiante> => {
      const { data } = await this.post<ApiEnvelope<Estudiante>>(
        `/api/admin/grupos/${grupoId}/estudiantes`,
        input,
        { conColegio: false }
      );
      return data;
    },

    // Carga masiva desde .xlsx (ver descargarPlantillaEstudiantes para el
    // formato). No es todo-o-nada: revisa `errores` para las filas que
    // fallaron (p. ej. CURP duplicado) sin bloquear las demás.
    importarEstudiantes: async (grupoId: string, archivo: File): Promise<ImportacionEstudiantes> => {
      const form = new FormData();
      form.set("archivo", archivo);
      const { data } = await this.post<ApiEnvelope<ImportacionEstudiantes>>(
        `/api/admin/grupos/${grupoId}/estudiantes/importar`,
        form,
        { conColegio: false }
      );
      return data;
    },

    descargarPlantillaEstudiantes: async (): Promise<{ blob: Blob; filename: string }> => {
      const res = await fetch("/api/admin/plantillas/estudiantes");
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message = body && typeof body === "object" && "error" in body ? String(body.error) : `Error ${res.status}`;
        throw new ApiError(res.status, message, body);
      }
      return { blob: await res.blob(), filename: "plantilla-estudiantes.xlsx" };
    },

    eliminarEstudiante: async (id: string): Promise<{ id: string; eliminado: boolean }> => {
      const { data } = await this.del<ApiEnvelope<{ id: string; eliminado: boolean }>>(
        `/api/admin/estudiantes/${id}`,
        { conColegio: false }
      );
      return data;
    },

    obtenerRespuestasGrupo: async (grupoId: string): Promise<GrupoRespuestas> => {
      const { data } = await this.get<ApiEnvelope<GrupoRespuestas>>(
        `/api/admin/grupos/${grupoId}/respuestas`,
        { conColegio: false }
      );
      return data;
    },

    obtenerGrupo: async (id: string): Promise<GrupoConProgreso & { estudiantes: EstudianteConEstado[] }> => {
      const { data } = await this.get<ApiEnvelope<GrupoConProgreso & { estudiantes: EstudianteConEstado[] }>>(
        `/api/admin/grupos/${id}`,
        { conColegio: false }
      );
      return data;
    },

    actualizarGrupo: async (
      id: string,
      cambios: Partial<{ nombre: string; formEmocionesId: string; formBienpsicId: string; formAprendizajeId: string }>
    ): Promise<Grupo> => {
      const { data } = await this.patch<ApiEnvelope<Grupo>>(`/api/admin/grupos/${id}`, cambios, { conColegio: false });
      return data;
    },

    eliminarGrupo: async (id: string): Promise<{ id: string; eliminado: boolean }> => {
      const { data } = await this.del<ApiEnvelope<{ id: string; eliminado: boolean }>>(
        `/api/admin/grupos/${id}`,
        { conColegio: false }
      );
      return data;
    },

    listarReportes: async (filtros: { evaluacionId?: string; tipo?: TipoReporte } = {}): Promise<Reporte[]> => {
      const { data } = await this.get<ApiEnvelope<Reporte[]>>(
        `/api/admin/reportes${buildQuery(filtros)}`,
        { conColegio: false }
      );
      return data;
    },

    listarArchivosReportes: async (evaluacionId: string, folder = ""): Promise<DirectorioReportes> => {
      const { data } = await this.get<ApiEnvelope<DirectorioReportes>>(
        `/api/admin/reportes/archivos-storage${buildQuery({ evaluacionId, folder })}`,
        { conColegio: false }
      );
      return data;
    },

    subirArchivosReportes: async (evaluacionId: string, archivos: File[], folder = ""): Promise<{ subidos: number; archivos: string[] }> => {
      const form = new FormData();
      form.set("evaluacionId", evaluacionId);
      form.set("folder", folder);
      archivos.forEach(archivo => form.append("archivos", archivo));

      const { data } = await this.post<ApiEnvelope<{ subidos: number; archivos: string[] }>>(
        "/api/admin/reportes/archivos-storage",
        form,
        { conColegio: false }
      );
      return data;
    },

    crearCarpetaReportes: async (evaluacionId: string, folder: string, folderName: string): Promise<{ creada: string }> => {
      const form = new FormData();
      form.set("evaluacionId", evaluacionId);
      form.set("folder", folder);
      form.set("folderName", folderName);

      const { data } = await this.post<ApiEnvelope<{ creada: string }>>(
        "/api/admin/reportes/archivos-storage",
        form,
        { conColegio: false }
      );
      return data;
    },

    eliminarArchivosReportes: async (evaluacionId: string, folder: string, keys: string[]): Promise<{ eliminados: number }> => {
      const { data } = await this.request<ApiEnvelope<{ eliminados: number }>>(
        "/api/admin/reportes/archivos-storage",
        {
          method: "DELETE",
          body: JSON.stringify({ evaluacionId, folder, keys }),
        },
        { conColegio: false }
      );
      return data;
    },

    subirReporte: async (input: {
      archivo: File;
      tipo: TipoReporte;
      evaluacionId: string;
      grupoId?: string;
      estudianteId?: string;
    }): Promise<Reporte> => {
      const form = new FormData();
      form.set("archivo", input.archivo);
      form.set("tipo", input.tipo);
      form.set("evaluacionId", input.evaluacionId);
      if (input.grupoId) form.set("grupoId", input.grupoId);
      if (input.estudianteId) form.set("estudianteId", input.estudianteId);

      const { data } = await this.post<ApiEnvelope<Reporte>>("/api/admin/reportes", form, { conColegio: false });
      return data;
    },

    subirReportesBulk: async (evaluacionId: string, archivos: File[]): Promise<{
      resultados: import("../utils/types").ResultadoReporteBulk[];
      asignados: number;
    }> => {
      const form = new FormData();
      form.set("evaluacionId", evaluacionId);
      archivos.forEach(archivo => form.append("archivos", archivo));

      const { data } = await this.post<ApiEnvelope<{
        resultados: import("../utils/types").ResultadoReporteBulk[];
        asignados: number;
      }>>("/api/admin/reportes/bulk", form, { conColegio: false });
      return data;
    },
  };

  // ── Facilitador ──────────────────────────────────────────────
  // Todas estas rutas (salvo verificar) requieren la sesión iniciada
  // con facilitador.verificar(), que guarda el X-Colegio-Id.
  facilitador = {
    verificar: async (claveAcceso: string): Promise<{
      colegio: { id: string; nombre: string };
      evaluaciones: EvaluacionConProgreso[];
    }> => {
      const { data } = await this.post<ApiEnvelope<{
        colegio: { id: string; nombre: string };
        evaluaciones: EvaluacionConProgreso[];
        token: string;
      }>>("/api/facilitador/verificar", { claveAcceso }, { conColegio: false });

      this.guardarSesionFacilitador(data.colegio.id, data.token);
      return { colegio: data.colegio, evaluaciones: data.evaluaciones };
    },

    // Info pública de una evaluación (nombre, colegio y estado).
    obtenerInfoEvaluacion: async (evaluacionId: string): Promise<EvaluacionParaFacilitador> => {
      const { data } = await this.get<ApiEnvelope<EvaluacionParaFacilitador>>(
        `/api/facilitador/evaluaciones/${evaluacionId}`,
        { conColegio: false }
      );
      return data;
    },

    // El enlace corto permite entrar a la encuesta sin pedir la clave del colegio.
    entrarPorEvaluacion: async (evaluacionId: string): Promise<EvaluacionParaFacilitador> => {
      const { data } = await this.post<ApiEnvelope<EvaluacionParaFacilitador & { colegioId: string; token: string }>>(
        `/api/facilitador/evaluaciones/${evaluacionId}`,
        undefined,
        { conColegio: false }
      );
      this.guardarSesionFacilitador(data.colegioId, data.token);
      return data;
    },

    // Verifica la clave de acceso escopeada a una evaluación puntual —
    // se usa en /reportes/:id. A diferencia de entrarPorEvaluacion(), aquí
    // SÍ hace falta la clave: el link solo indica qué evaluación ver.
    verificarConEvaluacion: async (
      evaluacionId: string,
      claveAcceso: string
    ): Promise<{ colegio: { id: string; nombre: string }; evaluacion: { id: string; nombre: string } }> => {
      const { data } = await this.post<ApiEnvelope<{
        colegio: { id: string; nombre: string };
        evaluacion: { id: string; nombre: string };
        token: string;
      }>>(`/api/facilitador/evaluaciones/${evaluacionId}/verificar`, { claveAcceso }, { conColegio: false });

      this.guardarSesionFacilitador(data.colegio.id, data.token);
      return { colegio: data.colegio, evaluacion: data.evaluacion };
    },

    listarGrupos: async (evaluacionId: string): Promise<{ evaluacion: Evaluacion; grupos: GrupoConProgreso[] }> => {
      const { data } = await this.get<ApiEnvelope<{ evaluacion: Evaluacion; grupos: GrupoConProgreso[] }>>(
        `/api/facilitador/grupos${buildQuery({ evaluacionId })}`
      );
      return data;
    },

    listarEstudiantes: async (
      grupoId: string,
      evaluacionId: string
    ): Promise<{ grupo: { id: string; nombre: string }; estudiantes: EstudianteConEstado[] }> => {
      const { data } = await this.get<ApiEnvelope<{ grupo: { id: string; nombre: string }; estudiantes: EstudianteConEstado[] }>>(
        `/api/facilitador/estudiantes/${grupoId}${buildQuery({ evaluacionId })}`
      );
      return data;
    },

    obtenerSesion: async (sesionId: string): Promise<{ sesion: Sesion & { estudianteNombre: string; formularioTitulo: string }; preguntas: PreguntaSesion[] }> => {
      const { data } = await this.get<ApiEnvelope<{ sesion: Sesion & { estudianteNombre: string; formularioTitulo: string }; preguntas: PreguntaSesion[] }>>(
        `/api/facilitador/sesiones${buildQuery({ sesionId })}`
      );
      return data;
    },

    // Crea o retoma la sesión de un alumno para un formulario dado.
    iniciarSesion: async (input: {
      estudianteId: string;
      formularioId: string;
      evaluacionId: string;
    }): Promise<{ sesion: { sesionId: string; estado: string; esNueva: boolean }; preguntas: PreguntaSesion[] }> => {
      const { data } = await this.post<ApiEnvelope<{ sesion: { sesionId: string; estado: string; esNueva: boolean }; preguntas: PreguntaSesion[] }>>(
        "/api/facilitador/sesiones",
        input
      );
      return data;
    },

    guardarRespuesta: async (input: {
      sesionId: string;
      preguntaId: string;
      textoLibre: string;
    }): Promise<{ respuestaId: string; respondidaAt: string }> => {
      const { data } = await this.post<ApiEnvelope<{ respuestaId: string; respondidaAt: string }>>(
        "/api/facilitador/sesiones/respuesta",
        input
      );
      return data;
    },

    // Si faltan preguntas por responder el backend devuelve 409 con
    // { total, respondidas } en err.detalle — captúralo con try/catch.
    completarSesion: async (sesionId: string): Promise<{ sesionId: string; completada: true }> => {
      const { data } = await this.patch<ApiEnvelope<{ sesionId: string; completada: true }>>(
        "/api/facilitador/sesiones/completar",
        { sesionId }
      );
      return data;
    },

    listarReportes: async (
      filtros: { evaluacionId?: string; tipo?: TipoReporte; grupoId?: string } = {}
    ): Promise<{ total: number; reportes: Record<"general" | "grupal" | "individual", ReporteConContexto[]> }> => {
      const { data } = await this.get<ApiEnvelope<{ total: number; reportes: Record<"general" | "grupal" | "individual", ReporteConContexto[]> }>>(
        `/api/facilitador/reportes${buildQuery(filtros)}`
      );
      return data;
    },

    listarArchivosReportes: async (evaluacionId: string, folder = ""): Promise<DirectorioReportes> => {
      const { data } = await this.get<ApiEnvelope<DirectorioReportes>>(
        `/api/facilitador/reportes/archivos-storage${buildQuery({ evaluacionId, folder })}`
      );
      return data;
    },
  };
}

export const databaseService = new DatabaseService();
export default databaseService;
